/**
 * ethminions.php 와 동일 파일명 — minionsView/public 또는 배포 루트에 두면 로드됩니다.
 * 없으면 자동으로 그래픽 폴백만 사용합니다.
 */
import * as PIXI from 'pixi.js';

/** file:// 에서 `/foo` 가 `file:///C:/foo` 로 깨지지 않게 BASE_URL(보통 `./`) 기준 */
function publicAssetUrl(path: string): string {
  const p = path.startsWith('/') ? path.slice(1) : path;
  return `${import.meta.env.BASE_URL}${p}`;
}

export const MINIONS_ASSET_URLS = {
  miniback: publicAssetUrl('miniback.png'),
  boxt: publicAssetUrl('boxt.png'),
  left: publicAssetUrl('left.png'),
  right: publicAssetUrl('right.png'),
  odd: publicAssetUrl('odd.png'),
  even: publicAssetUrl('even.png'),
  solo: publicAssetUrl('solo.gif'),
  team: publicAssetUrl('team.gif'),
} as const;

export type MinionsAssetKey = keyof typeof MINIONS_ASSET_URLS;

export type MinionsTextureSet = Partial<Record<MinionsAssetKey, PIXI.Texture>>;

/**
 * file:// — Chrome 이 HTMLImageElement 를 WebGL texImage2D 에 올릴 때 SecurityError 를 내는 경우가 있어,
 * `createImageBitmap(img)` 경로는 환경에 따라 "cross-origin data" 로 막히는 경우가 있어
 * 캔버스 기반 텍스처로만 만듦.
 */
function loadImageTextureFile(url: string): Promise<PIXI.Texture | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      void (async () => {
        try {
          const w = img.naturalWidth || img.width;
          const h = img.naturalHeight || img.height;
          if (!w || !h) {
            resolve(null);
            return;
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(null);
            return;
          }
          ctx.drawImage(img, 0, 0);
          resolve(PIXI.Texture.from(canvas));
        } catch {
          resolve(null);
        }
      })();
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function loadImageTexture(url: string): Promise<PIXI.Texture | null> {
  const isFile =
    typeof globalThis.location?.protocol === 'string' &&
    globalThis.location.protocol === 'file:';
  if (isFile) return loadImageTextureFile(url);
  return PIXI.Texture.fromURL(url).catch(() => null);
}

/** 병렬 로드 — 실패한 URL 은 결과에서 생략 */
export async function loadMinionsTextureSet(): Promise<MinionsTextureSet> {
  const entries = await Promise.all(
    (Object.keys(MINIONS_ASSET_URLS) as MinionsAssetKey[]).map(async (key) => {
      const url = MINIONS_ASSET_URLS[key];
      const tex = await loadImageTexture(url);
      return [key, tex] as const;
    })
  );
  const out: MinionsTextureSet = {};
  for (const [k, tex] of entries) {
    if (tex) out[k] = tex;
  }
  return out;
}
